import { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { motion } from 'framer-motion';
import { CreditCard, Truck, ChevronLeft, Check, User } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { RadioGroup, RadioGroupItem } from '../components/ui/radio-group';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { useCart } from '../context/CartContext';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Romanian Counties (Județe)
const ROMANIAN_COUNTIES = [
  'Alba', 'Arad', 'Argeș', 'Bacău', 'Bihor', 'Bistrița-Năsăud', 'Botoșani', 
  'Brașov', 'Brăila', 'București', 'Buzău', 'Caraș-Severin', 'Călărași', 
  'Cluj', 'Constanța', 'Covasna', 'Dâmbovița', 'Dolj', 'Galați', 'Giurgiu', 
  'Gorj', 'Harghita', 'Hunedoara', 'Ialomița', 'Iași', 'Ilfov', 'Maramureș', 
  'Mehedinți', 'Mureș', 'Neamț', 'Olt', 'Prahova', 'Satu Mare', 'Sălaj', 
  'Sibiu', 'Suceava', 'Teleorman', 'Timiș', 'Tulcea', 'Vaslui', 'Vâlcea', 'Vrancea'
];

const CheckoutPage = () => {
  const navigate = useNavigate();
  const { cart, clearCart, guestCart, clearGuestCart } = useCart();
  const { token, isAuthenticated } = useAuth();
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState('cod');
  const [isGuest, setIsGuest] = useState(!isAuthenticated);
  
  const [address, setAddress] = useState({
    full_name: '',
    phone: '',
    email: '',
    address: '',
    city: '',
    county: '',
    postal_code: ''
  });

  // Get the appropriate cart (guest or authenticated)
  const activeCart = isAuthenticated ? cart : guestCart;

  useEffect(() => {
    // Allow both authenticated and guest users
    if (!activeCart || activeCart.items?.length === 0) {
      // Check if there's no cart at all
      if (!isAuthenticated && (!guestCart || guestCart.items?.length === 0)) {
        navigate('/cart');
      } else if (isAuthenticated && (!cart || cart.items?.length === 0)) {
        navigate('/cart');
      }
    }
  }, [isAuthenticated, cart, guestCart, navigate, activeCart]);

  const handleAddressChange = (e) => {
    setAddress(prev => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const fieldLabels = {
    full_name: 'Nume complet',
    phone: 'Telefon',
    email: 'Email',
    address: 'Adresă',
    city: 'Oraș',
    county: 'Județ',
    postal_code: 'Cod poștal'
  };

  const validateAddress = () => {
    // Email is now required for ALL checkouts
    const required = ['full_name', 'email', 'phone', 'address', 'city', 'county', 'postal_code'];
    for (const field of required) {
      if (!address[field]?.trim()) {
        toast.error(`Te rugăm să completezi ${fieldLabels[field]}`);
        return false;
      }
    }
    // Validate email format
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(address.email)) {
      toast.error('Te rugăm să introduci un email valid');
      return false;
    }
    return true;
  };

  const handleSubmit = async () => {
    if (!validateAddress()) return;
    
    setLoading(true);
    try {
      if (isAuthenticated) {
        // Logged in user checkout
        const orderRes = await axios.post(`${API}/orders`, {
          address,
          payment_method: paymentMethod
        }, {
          headers: { Authorization: `Bearer ${token}` }
        });

        const orderId = orderRes.data.id;

        if (paymentMethod === 'stripe') {
          const checkoutRes = await axios.post(`${API}/checkout/stripe`, {
            order_id: orderId,
            origin_url: window.location.origin
          }, {
            headers: { Authorization: `Bearer ${token}` }
          });
          window.location.href = checkoutRes.data.url;
        } else {
          await axios.post(`${API}/orders/${orderId}/confirm-cod`, {}, {
            headers: { Authorization: `Bearer ${token}` }
          });
          toast.success('Comandă plasată cu succes!');
          navigate('/profile');
        }
      } else {
        // Guest checkout
        const guestOrderData = {
          address,
          payment_method: paymentMethod,
          items: guestCart.items.map(item => ({
            product_id: item.product_id,
            size: item.size,
            quantity: item.quantity
          }))
        };

        const orderRes = await axios.post(`${API}/orders/guest`, guestOrderData);
        const orderId = orderRes.data.id;

        if (paymentMethod === 'stripe') {
          const checkoutRes = await axios.post(`${API}/checkout/guest/stripe?order_id=${orderId}&origin_url=${encodeURIComponent(window.location.origin)}`);
          clearGuestCart();
          window.location.href = checkoutRes.data.url;
        } else {
          await axios.post(`${API}/orders/guest/${orderId}/confirm-cod`);
          clearGuestCart();
          toast.success('Comandă plasată cu succes! Vei primi o confirmare pe email.');
          navigate(`/checkout/success?order_id=${orderId}`);
        }
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Eroare la plasarea comenzii');
    } finally {
      setLoading(false);
    }
  };

  // Calculate totals from active cart
  const cartItems = activeCart?.items || [];
  const subtotal = cartItems.reduce((sum, item) => sum + (item.price * item.quantity), 0);
  const total = subtotal;

  if (cartItems.length === 0) {
    return null;
  }

  return (
    <div className="min-h-screen bg-[#0a0a0a]">
      <div className="px-6 md:px-12 lg:px-24 py-12">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          {/* Progress Steps */}
          <div className="flex items-center justify-center gap-4 mb-12">
            {[1, 2, 3].map((s) => (
              <div key={s} className="flex items-center gap-4">
                <div className={`w-10 h-10 flex items-center justify-center font-heading font-bold ${
                  step >= s ? 'bg-[#c00000] text-white' : 'bg-white/10 text-white/50'
                }`}>
                  {step > s ? <Check className="w-5 h-5" /> : s}
                </div>
                {s < 3 && <div className={`w-16 h-0.5 ${step > s ? 'bg-[#c00000]' : 'bg-white/10'}`} />}
              </div>
            ))}
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
            {/* Main Form */}
            <div className="lg:col-span-2">
              {step === 1 && (
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  className="bg-[#121212] border border-white/5 p-8"
                >
                  <h2 className="font-heading font-bold text-2xl tracking-wider text-white mb-8">
                    INFORMAȚII LIVRARE
                  </h2>

                  {/* Guest checkout notice */}
                  {!isAuthenticated && (
                    <div className="bg-[#0a0a0a] border border-white/10 p-4 mb-6">
                      <div className="flex items-center gap-3">
                        <User className="w-5 h-5 text-[#c00000]" />
                        <div>
                          <p className="font-body text-white text-sm">Cumperi ca vizitator</p>
                          <p className="font-body text-white/50 text-xs">
                            Ai deja cont? <Link to="/login" className="text-[#c00000] hover:underline">Conectează-te</Link> pentru a urmări comenzile
                          </p>
                        </div>
                      </div>
                    </div>
                  )}
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="md:col-span-2">
                      <Label className="font-body text-white/70 mb-2 block">Nume Complet *</Label>
                      <Input
                        name="full_name"
                        value={address.full_name}
                        onChange={handleAddressChange}
                        className="bg-[#0a0a0a] border-white/10 text-white rounded-none h-12"
                        placeholder="Numele tău complet"
                        data-testid="full-name-input"
                      />
                    </div>
                    
                    {/* Email field - required for all users */}
                    <div className="md:col-span-2">
                      <Label className="font-body text-white/70 mb-2 block">Adresă Email * (pentru confirmarea comenzii)</Label>
                      <Input
                        name="email"
                        type="email"
                        value={address.email}
                        onChange={handleAddressChange}
                        className="bg-[#0a0a0a] border-white/10 text-white rounded-none h-12"
                        placeholder="emailul@tau.com"
                        data-testid="email-input"
                      />
                    </div>

                    <div>
                      <Label className="font-body text-white/70 mb-2 block">Telefon *</Label>
                      <Input
                        name="phone"
                        value={address.phone}
                        onChange={handleAddressChange}
                        className="bg-[#0a0a0a] border-white/10 text-white rounded-none h-12"
                        placeholder="07XX XXX XXX"
                        data-testid="phone-input"
                      />
                    </div>
                    <div>
                      <Label className="font-body text-white/70 mb-2 block">Cod Poștal *</Label>
                      <Input
                        name="postal_code"
                        value={address.postal_code}
                        onChange={handleAddressChange}
                        className="bg-[#0a0a0a] border-white/10 text-white rounded-none h-12"
                        placeholder="XXXXXX"
                        data-testid="postal-code-input"
                      />
                    </div>
                    <div className="md:col-span-2">
                      <Label className="font-body text-white/70 mb-2 block">Adresă *</Label>
                      <Input
                        name="address"
                        value={address.address}
                        onChange={handleAddressChange}
                        className="bg-[#0a0a0a] border-white/10 text-white rounded-none h-12"
                        placeholder="Strada, număr, apartament"
                        data-testid="address-input"
                      />
                    </div>
                    <div>
                      <Label className="font-body text-white/70 mb-2 block">Oraș *</Label>
                      <Input
                        name="city"
                        value={address.city}
                        onChange={handleAddressChange}
                        className="bg-[#0a0a0a] border-white/10 text-white rounded-none h-12"
                        placeholder="Oraș"
                        data-testid="city-input"
                      />
                    </div>
                    <div>
                      <Label className="font-body text-white/70 mb-2 block">Județ *</Label>
                      <Select
                        value={address.county}
                        onValueChange={(value) => setAddress(prev => ({ ...prev, county: value }))}
                      >
                        <SelectTrigger 
                          className="bg-[#0a0a0a] border-white/10 text-white rounded-none h-12"
                          data-testid="county-select"
                        >
                          <SelectValue placeholder="Selectează județul" />
                        </SelectTrigger>
                        <SelectContent className="bg-[#121212] border-white/10 max-h-[300px]">
                          {ROMANIAN_COUNTIES.map((county) => (
                            <SelectItem 
                              key={county} 
                              value={county}
                              className="text-white hover:bg-white/10"
                            >
                              {county}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  <Button
                    onClick={() => validateAddress() && setStep(2)}
                    className="w-full mt-8 bg-[#c00000] text-white hover:bg-[#a00000] rounded-none py-6 font-heading font-bold tracking-wider"
                    data-testid="continue-btn"
                  >
                    CONTINUĂ LA PLATĂ
                  </Button>
                </motion.div>
              )}

              {step === 2 && (
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  className="bg-[#121212] border border-white/5 p-8"
                >
                  <button
                    onClick={() => setStep(1)}
                    className="flex items-center gap-2 font-body text-sm text-white/50 hover:text-white mb-6 transition-colors"
                  >
                    <ChevronLeft className="w-4 h-4" /> Înapoi la Livrare
                  </button>

                  <h2 className="font-heading font-bold text-2xl tracking-wider text-white mb-8">
                    METODĂ DE PLATĂ
                  </h2>

                  <RadioGroup value={paymentMethod} onValueChange={setPaymentMethod} className="space-y-4">
                    {/* Cash on Delivery */}
                    <label 
                      className={`flex items-center gap-4 p-6 border cursor-pointer transition-colors ${
                        paymentMethod === 'cod' ? 'border-[#c00000] bg-[#c00000]/5' : 'border-white/10 hover:border-white/20'
                      }`}
                      data-testid="cod-option"
                    >
                      <RadioGroupItem value="cod" className="border-white/30 text-[#c00000]" />
                      <Truck className="w-6 h-6 text-white/70" />
                      <div className="flex-1">
                        <h3 className="font-heading font-bold text-white">Plata la Livrare</h3>
                        <p className="font-body text-sm text-white/50">Plătești când primești coletul. Verifici înainte de plată.</p>
                      </div>
                    </label>

                    {/* Card Payment */}
                    <label 
                      className={`flex items-start gap-4 p-6 border cursor-pointer transition-colors ${
                        paymentMethod === 'stripe' ? 'border-[#c00000] bg-[#c00000]/5' : 'border-white/10 hover:border-white/20'
                      }`}
                      data-testid="stripe-option"
                    >
                      <RadioGroupItem value="stripe" className="border-white/30 text-[#c00000] mt-1" />
                      <CreditCard className="w-6 h-6 text-white/70 mt-1" />
                      <div className="flex-1">
                        <h3 className="font-heading font-bold text-white">Plată cu Cardul</h3>
                        <p className="font-body text-sm text-white/50 mb-3">Plată securizată prin Stripe</p>
                        
                        {/* Payment method icons */}
                        <div className="flex flex-wrap items-center gap-3">
                          <div className="bg-white px-2 py-1 rounded">
                            <svg viewBox="0 0 48 32" className="h-5 w-auto">
                              <rect fill="#1A1F71" width="48" height="32" rx="4"/>
                              <path fill="#FFFFFF" d="M19.5 21h-2.9l1.8-11h2.9l-1.8 11zm12.1-10.7c-.6-.2-1.5-.5-2.6-.5-2.9 0-4.9 1.5-4.9 3.7 0 1.6 1.5 2.5 2.6 3.1 1.1.6 1.5 1 1.5 1.5 0 .8-1 1.2-1.8 1.2-1.2 0-1.9-.2-2.9-.6l-.4-.2-.4 2.6c.7.3 2.1.6 3.5.6 3.1 0 5.1-1.5 5.1-3.8 0-1.3-.8-2.2-2.5-3-1-.5-1.7-.9-1.7-1.4 0-.5.5-1 1.7-1 1 0 1.7.2 2.2.4l.3.1.4-2.5z"/>
                            </svg>
                          </div>
                          <div className="bg-white px-2 py-1 rounded">
                            <svg viewBox="0 0 48 32" className="h-5 w-auto">
                              <rect fill="#000000" width="48" height="32" rx="4"/>
                              <circle fill="#EB001B" cx="18" cy="16" r="9"/>
                              <circle fill="#F79E1B" cx="30" cy="16" r="9"/>
                            </svg>
                          </div>
                          <div className="bg-black px-2 py-1 rounded border border-white/20">
                            <span className="text-white text-xs font-bold">Apple Pay</span>
                          </div>
                          <div className="bg-white px-2 py-1 rounded">
                            <span className="text-gray-700 text-xs font-bold">Google Pay</span>
                          </div>
                        </div>
                      </div>
                    </label>
                  </RadioGroup>

                  <Button
                    onClick={() => setStep(3)}
                    className="w-full mt-8 bg-[#c00000] text-white hover:bg-[#a00000] rounded-none py-6 font-heading font-bold tracking-wider"
                    data-testid="review-btn"
                  >
                    VERIFICĂ COMANDA
                  </Button>
                </motion.div>
              )}

              {step === 3 && (
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  className="bg-[#121212] border border-white/5 p-8"
                >
                  <button
                    onClick={() => setStep(2)}
                    className="flex items-center gap-2 font-body text-sm text-white/50 hover:text-white mb-6 transition-colors"
                  >
                    <ChevronLeft className="w-4 h-4" /> Înapoi la Plată
                  </button>

                  <h2 className="font-heading font-bold text-2xl tracking-wider text-white mb-8">
                    VERIFICĂ COMANDA
                  </h2>

                  {/* Shipping Summary */}
                  <div className="mb-6 pb-6 border-b border-white/5">
                    <h3 className="font-heading font-bold text-sm tracking-wider text-white/70 mb-3">LIVRARE LA</h3>
                    <p className="font-body text-white">{address.full_name}</p>
                    {address.email && <p className="font-body text-white/60">{address.email}</p>}
                    <p className="font-body text-white/60">{address.address}</p>
                    <p className="font-body text-white/60">{address.city}, {address.county} {address.postal_code}</p>
                    <p className="font-body text-white/60">{address.phone}</p>
                  </div>

                  {/* Payment Summary */}
                  <div className="mb-6 pb-6 border-b border-white/5">
                    <h3 className="font-heading font-bold text-sm tracking-wider text-white/70 mb-3">PLATĂ</h3>
                    <p className="font-body text-white">
                      {paymentMethod === 'cod' ? 'Plata la Livrare' : 'Plată cu Cardul (Stripe)'}
                    </p>
                  </div>

                  {/* Items */}
                  <div className="mb-6">
                    <h3 className="font-heading font-bold text-sm tracking-wider text-white/70 mb-3">PRODUSE</h3>
                    <div className="space-y-4">
                      {cartItems.map((item, idx) => (
                        <div key={idx} className="flex items-center gap-4">
                          <div className="w-16 h-16 bg-[#0a0a0a]">
                            <img src={item.product_image || item.image} alt="" className="w-full h-full object-cover" />
                          </div>
                          <div className="flex-1">
                            <p className="font-body text-sm text-white">{item.product_name || item.name}</p>
                            <p className="font-body text-xs text-white/50">Mărime {item.size} × {item.quantity}</p>
                          </div>
                          <span className="font-heading font-bold text-white">{(item.price * item.quantity).toFixed(2)} RON</span>
                        </div>
                      ))}
                    </div>
                  </div>

                  <Button
                    onClick={handleSubmit}
                    disabled={loading}
                    className="w-full bg-[#c00000] text-white hover:bg-[#a00000] rounded-none py-6 font-heading font-bold tracking-wider"
                    data-testid="place-order-btn"
                  >
                    {loading ? 'SE PROCESEAZĂ...' : paymentMethod === 'stripe' ? 'PLĂTEȘTE ACUM' : 'PLASEAZĂ COMANDA'}
                  </Button>
                </motion.div>
              )}
            </div>

            {/* Order Summary Sidebar */}
            <div className="lg:col-span-1">
              <div className="bg-[#121212] border border-white/5 p-8 sticky top-28">
                <h2 className="font-heading font-bold text-xl tracking-wider text-white mb-6">SUMAR COMANDĂ</h2>
                
                <div className="space-y-4 pb-6 border-b border-white/5">
                  <div className="flex justify-between font-body text-white/60">
                    <span>Subtotal ({cartItems.length} {cartItems.length === 1 ? 'produs' : 'produse'})</span>
                    <span>{subtotal.toFixed(2)} RON</span>
                  </div>
                  <div className="flex justify-between font-body text-white/60">
                    <span>Livrare</span>
                    <span className="text-[#c00000]">GRATUITĂ</span>
                  </div>
                </div>

                <div className="flex justify-between items-center py-6">
                  <span className="font-heading font-bold text-lg text-white">TOTAL</span>
                  <span className="font-heading font-bold text-2xl text-[#c00000]" data-testid="checkout-total">
                    {total.toFixed(2)} RON
                  </span>
                </div>

                <div className="pt-6 border-t border-white/5 space-y-3">
                  <div className="flex items-center gap-2 text-white/40 text-sm">
                    <Check className="w-4 h-4 text-[#c00000]" />
                    <span className="font-body">Livrare gratuită în toată România</span>
                  </div>
                  <div className="flex items-center gap-2 text-white/40 text-sm">
                    <Check className="w-4 h-4 text-[#c00000]" />
                    <span className="font-body">Retur gratuit în 14 zile</span>
                  </div>
                  <div className="flex items-center gap-2 text-white/40 text-sm">
                    <Check className="w-4 h-4 text-[#c00000]" />
                    <span className="font-body">Calitate premium garantată</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default CheckoutPage;
